/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { createApi } from '@reduxjs/toolkit/dist/query/react'
import { mercuryBaseQuery } from 'api/base'
import { Invoice, PaymentMethodData } from 'models/billing'

/**********************************************************************************************************
 *   BILLING API
 **********************************************************************************************************/
export const billingAPI = createApi({
    reducerPath: 'billingAPI',
    baseQuery: mercuryBaseQuery,
    tagTypes: ['Payment-Methods', 'Invoices', 'Invoice'],
    endpoints: (builder) => ({
        accountPaymentMethods: builder.query<PaymentMethodData[], void>({
            query: () => 'client/billing/payment-method-data',
            transformResponse: (response: { data: PaymentMethodData[] }) => response.data,
            providesTags: ['Payment-Methods']
        }),
        accountPaymentMethod: builder.query<PaymentMethodData, number>({
            query: (id) => `client/billing/payment-method-data/${id}`,
            transformResponse: (response: { data: PaymentMethodData }) => response.data
        }),
        savePaymentMethod: builder.mutation({
            query: (paymentMethod) => ({
                url: 'client/billing/payment-method-data',
                method: 'POST',
                body: paymentMethod
            }),
            invalidatesTags: ['Payment-Methods']
        }),
        removePaymentMethod: builder.mutation({
            query: (paymentMethod) => ({
                url: `client/billing/payment-method-data/${paymentMethod}`,
                method: 'DELETE'
            }),
            invalidatesTags: ['Payment-Methods']
        }),
        accountCredit: builder.query<{ account_credit: number }, void>({
            query: () => 'client/billing/credit',
            transformResponse: (response: { data: { account_credit: number } }) => response.data
        }),
        invoices: builder.query({
            query: ({ page = 1, filter_by, sort_by, search_by }) => {
                return `client/billing/invoices?page=${page}${sort_by ? `&sort_by=${sort_by}` : ''}${filter_by ? `&filter_by=${filter_by.key},${filter_by.value}` : ''}${search_by ? `&search_by=${search_by.key},~,${search_by.value}` : ''}`
            },
            providesTags: ['Invoices']
        }),
        invoice: builder.query<Invoice, number | undefined>({
            query: (invoice) => `client/billing/invoices/${invoice}`,
            transformResponse: (response: { data: Invoice }) => response.data,
            providesTags: ['Invoice']
        }),
        payInvoice: builder.mutation({
            query: ({ id, ...rest }) => ({
                url: `client/billing/invoices/${id}/pay`,
                method: 'POST',
                body: rest
            }),
            invalidatesTags: ['Invoices']
        }),
        sendInvoiceEmail: builder.mutation({
            query: (id) => ({
                url: `client/billing/invoices/${id}/send`,
                method: 'POST'
            })
        }),
        transactions: builder.query({
            query: (page = 1) => `client/billing/transactions?page=${page}`
        })
    })
})

export const {
    useAccountPaymentMethodsQuery,
    useAccountPaymentMethodQuery,
    useSavePaymentMethodMutation,
    useRemovePaymentMethodMutation,
    useAccountCreditQuery,
    useInvoicesQuery,
    useInvoiceQuery,
    usePayInvoiceMutation,
    useSendInvoiceEmailMutation,
    useTransactionsQuery
} = billingAPI
